import type {
  PublicPlayer,
  VoiceChannel,
} from "@ma-soi/game-core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RoomSnapshot, VoiceSignalMessage } from "./useRoomConnection";

type AudioChannel = Exclude<VoiceChannel, "none">;

interface UseVoiceChatInput {
  roomId: string;
  userId: string | null;
  players: PublicPlayer[];
  voice: RoomSnapshot["voice"] | null;
  socketReady: boolean;
  sendSocketMessage: (message: unknown) => boolean;
  voiceSignal: VoiceSignalMessage | null;
}

interface RemoteVoiceStream {
  playerId: string;
  stream: MediaStream;
}

function rtcSupported() {
  return typeof window !== "undefined"
    && "RTCPeerConnection" in window
    && Boolean(navigator.mediaDevices?.getUserMedia);
}

function iceServers(): RTCIceServer[] {
  const fallback: RTCIceServer[] = [{ urls: "stun:stun.l.google.com:19302" }];
  const raw = import.meta.env.VITE_RTC_ICE_SERVERS;
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw) as RTCIceServer[];
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function isSessionDescription(value: unknown): value is RTCSessionDescriptionInit {
  if (!value || typeof value !== "object") return false;
  const type = (value as { type?: unknown }).type;
  return type === "offer" || type === "answer";
}

function isIceCandidate(value: unknown): value is RTCIceCandidateInit {
  return Boolean(value && typeof value === "object");
}

export function useVoiceChat({
  roomId,
  userId,
  players,
  voice,
  socketReady,
  sendSocketMessage,
  voiceSignal,
}: UseVoiceChatInput) {
  const configuration = useMemo<RTCConfiguration>(() => ({ iceServers: iceServers() }), []);
  const access = voice?.access ?? {
    channel: "none" as const,
    canListen: false,
    canSpeak: false,
    reason: "voice-disabled" as const,
  };
  const participants = voice?.participants ?? [];
  const settings = voice?.settings;
  const currentChannel = access.channel;
  const currentChannelRef = useRef<VoiceChannel>("none");
  const localStreamRef = useRef<MediaStream | null>(null);
  const peersRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const offeredPeersRef = useRef<Set<string>>(new Set());
  const joinedChannelRef = useRef<VoiceChannel>("none");
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [micEnabled, setMicEnabled] = useState(false);
  const [mutedBySelf, setMutedBySelf] = useState(false);
  const [pushToTalkPressed, setPushToTalkPressed] = useState(false);
  const [audioPermissionGranted, setAudioPermissionGranted] = useState(false);
  const [localSpeaking, setLocalSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remoteStreams, setRemoteStreams] = useState<RemoteVoiceStream[]>([]);
  const [masterVolume, setMasterVolumeState] = useState(() => {
    const saved = localStorage.getItem("ma-soi:voice-volume");
    return saved ? Number(saved) || 0.85 : 0.85;
  });
  const [peerVolumes, setPeerVolumes] = useState<Record<string, number>>({});

  const playerNames = useMemo(() => new Map(players.map((player) => [player.uid, player.name])), [players]);
  const canSendAudio = voiceEnabled
    && access.canSpeak
    && micEnabled
    && !mutedBySelf
    && (!settings?.pushToTalk || pushToTalkPressed);
  const browserSupported = rtcSupported();
  const activeParticipants = participants.filter((participant) => participant.playerId !== userId);
  const someoneSpeaking = localSpeaking || participants.some((participant) => participant.isSpeaking);

  useEffect(() => {
    currentChannelRef.current = currentChannel;
  }, [currentChannel]);

  const setMasterVolume = useCallback((value: number) => {
    const safe = Math.min(1, Math.max(0, value));
    setMasterVolumeState(safe);
    localStorage.setItem("ma-soi:voice-volume", String(safe));
  }, []);

  const setPeerVolume = useCallback((playerId: string, value: number) => {
    const safe = Math.min(1, Math.max(0, value));
    setPeerVolumes((current) => ({ ...current, [playerId]: safe }));
  }, []);

  const closePeer = useCallback((playerId: string) => {
    const peer = peersRef.current.get(playerId);
    if (peer) {
      peer.onicecandidate = null;
      peer.ontrack = null;
      peer.close();
      peersRef.current.delete(playerId);
    }
    setRemoteStreams((streams) => streams.filter((entry) => entry.playerId !== playerId));
  }, []);

  const closeAllPeers = useCallback(() => {
    for (const playerId of [...peersRef.current.keys()]) closePeer(playerId);
    offeredPeersRef.current.clear();
  }, [closePeer]);

  const sendVoiceState = useCallback((patch: Record<string, unknown>) => {
    if (!socketReady) return false;
    return sendSocketMessage({ type: "voice:state", ...patch });
  }, [sendSocketMessage, socketReady]);

  const addLocalTracks = useCallback((peer: RTCPeerConnection) => {
    const stream = localStreamRef.current;
    if (!stream) return;
    for (const track of stream.getAudioTracks()) {
      if (!peer.getSenders().some((sender) => sender.track === track)) {
        peer.addTrack(track, stream);
      }
    }
  }, []);

  const createPeer = useCallback((playerId: string) => {
    const existing = peersRef.current.get(playerId);
    if (existing) return existing;
    const peer = new RTCPeerConnection(configuration);
    peer.onicecandidate = (event) => {
      if (!event.candidate) return;
      const channel = currentChannelRef.current;
      if (channel === "none") return;
      sendSocketMessage({
        type: "voice:signal",
        toPlayerId: playerId,
        signalType: "ice-candidate",
        channel,
        payload: event.candidate.toJSON(),
      });
    };
    peer.ontrack = (event) => {
      const [stream] = event.streams;
      if (!stream) return;
      setRemoteStreams((current) => {
        const next = current.filter((entry) => entry.playerId !== playerId);
        return [...next, { playerId, stream }];
      });
    };
    peer.onconnectionstatechange = () => {
      if (peer.connectionState === "failed" || peer.connectionState === "closed") {
        closePeer(playerId);
      }
    };
    addLocalTracks(peer);
    peersRef.current.set(playerId, peer);
    return peer;
  }, [addLocalTracks, closePeer, configuration, sendSocketMessage]);

  const createOffer = useCallback(async (playerId: string) => {
    const channel = currentChannelRef.current;
    if (!userId || channel === "none") return;
    const peer = createPeer(playerId);
    addLocalTracks(peer);
    if (peer.signalingState !== "stable") return;
    const offer = await peer.createOffer({ offerToReceiveAudio: true });
    await peer.setLocalDescription(offer);
    sendSocketMessage({
      type: "voice:signal",
      toPlayerId: playerId,
      signalType: "offer",
      channel,
      payload: offer,
    });
  }, [addLocalTracks, createPeer, sendSocketMessage, userId]);

  const renegotiatePeers = useCallback(() => {
    if (!userId) return;
    const channel = currentChannelRef.current;
    if (channel === "none") return;
    for (const playerId of peersRef.current.keys()) {
      void createOffer(playerId).catch(() => setError("Không thể kết nối âm thanh. Vui lòng tải lại trang."));
    }
  }, [createOffer, userId]);

  const requestMicrophone = useCallback(async () => {
    if (!browserSupported) {
      setError("Trình duyệt của bạn chưa hỗ trợ WebRTC hoặc micro.");
      return null;
    }
    if (localStreamRef.current) {
      setAudioPermissionGranted(true);
      setMicEnabled(true);
      return localStreamRef.current;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      localStreamRef.current = stream;
      setAudioPermissionGranted(true);
      setMicEnabled(true);
      setError(null);
      for (const peer of peersRef.current.values()) addLocalTracks(peer);
      renegotiatePeers();
      return stream;
    } catch {
      setAudioPermissionGranted(false);
      setMicEnabled(false);
      setError("Trình duyệt của bạn chưa cấp quyền micro.");
      return null;
    }
  }, [addLocalTracks, browserSupported, renegotiatePeers]);

  const startVoice = useCallback(async () => {
    if (!access.canListen) {
      setError("Kênh thoại hiện không khả dụng trong giai đoạn này.");
      return;
    }
    if (access.canSpeak) {
      const stream = await requestMicrophone();
      if (!stream) return;
      setMutedBySelf(false);
    }
    setVoiceEnabled(true);
  }, [access.canListen, access.canSpeak, requestMicrophone]);

  const leaveVoice = useCallback(() => {
    setVoiceEnabled(false);
    setLocalSpeaking(false);
    closeAllPeers();
    if (socketReady) sendSocketMessage({ type: "voice:leave" });
    joinedChannelRef.current = "none";
  }, [closeAllPeers, sendSocketMessage, socketReady]);

  const toggleMute = useCallback(() => {
    setMutedBySelf((current) => !current);
  }, []);

  useEffect(() => {
    for (const track of localStreamRef.current?.getAudioTracks() ?? []) {
      track.enabled = canSendAudio;
    }
  }, [canSendAudio]);

  useEffect(() => {
    if (!voiceEnabled || !userId || !socketReady || !access.canListen || currentChannel === "none") {
      if (joinedChannelRef.current !== "none") {
        sendSocketMessage({ type: "voice:leave" });
        joinedChannelRef.current = "none";
      }
      closeAllPeers();
      return;
    }
    if (joinedChannelRef.current !== currentChannel) {
      closeAllPeers();
      offeredPeersRef.current.clear();
      joinedChannelRef.current = currentChannel;
    }
    sendSocketMessage({
      type: "voice:join",
      channel: currentChannel,
      mutedBySelf,
      audioPermissionGranted,
    });
  }, [
    access.canListen,
    audioPermissionGranted,
    closeAllPeers,
    currentChannel,
    mutedBySelf,
    sendSocketMessage,
    socketReady,
    userId,
    voiceEnabled,
  ]);

  useEffect(() => {
    if (!voiceEnabled || !socketReady || currentChannel === "none") return;
    sendVoiceState({ mutedBySelf, audioPermissionGranted });
  }, [audioPermissionGranted, currentChannel, mutedBySelf, sendVoiceState, socketReady, voiceEnabled]);

  useEffect(() => {
    if (!voiceEnabled || !userId || !access.canListen || currentChannel === "none") return;
    const activeIds = new Set(activeParticipants.map((participant) => participant.playerId));
    for (const playerId of [...peersRef.current.keys()]) {
      if (!activeIds.has(playerId)) closePeer(playerId);
    }
    for (const participant of activeParticipants) {
      const peer = createPeer(participant.playerId);
      const offerKey = `${currentChannel}:${participant.playerId}`;
      if (userId < participant.playerId && !offeredPeersRef.current.has(offerKey)) {
        offeredPeersRef.current.add(offerKey);
        if (peer.signalingState === "stable") {
          void createOffer(participant.playerId).catch(() => {
            setError("Không thể kết nối âm thanh. Vui lòng tải lại trang.");
          });
        }
      }
    }
  }, [
    access.canListen,
    activeParticipants,
    closePeer,
    createOffer,
    createPeer,
    currentChannel,
    userId,
    voiceEnabled,
  ]);

  useEffect(() => {
    if (!voiceSignal || !voiceEnabled || !userId) return;
    if (voiceSignal.channel !== currentChannelRef.current || voiceSignal.fromPlayerId === userId) return;
    const handleSignal = async () => {
      const peer = createPeer(voiceSignal.fromPlayerId);
      if (voiceSignal.signalType === "offer") {
        if (!isSessionDescription(voiceSignal.payload)) return;
        await peer.setRemoteDescription(voiceSignal.payload);
        addLocalTracks(peer);
        const answer = await peer.createAnswer();
        await peer.setLocalDescription(answer);
        sendSocketMessage({
          type: "voice:signal",
          toPlayerId: voiceSignal.fromPlayerId,
          signalType: "answer",
          channel: voiceSignal.channel,
          payload: answer,
        });
      } else if (voiceSignal.signalType === "answer") {
        if (!isSessionDescription(voiceSignal.payload)) return;
        if (peer.signalingState !== "stable") await peer.setRemoteDescription(voiceSignal.payload);
      } else if (voiceSignal.signalType === "ice-candidate") {
        if (!isIceCandidate(voiceSignal.payload)) return;
        await peer.addIceCandidate(voiceSignal.payload);
      }
    };
    void handleSignal().catch(() => setError("Không thể kết nối âm thanh. Vui lòng tải lại trang."));
  }, [addLocalTracks, createPeer, sendSocketMessage, userId, voiceEnabled, voiceSignal]);

  useEffect(() => {
    if (!settings?.voiceActivityDetection || !localStreamRef.current) return;
    let disposed = false;
    let animationFrame = 0;
    let lastSpeaking = false;
    let lastSentAt = 0;
    const context = new AudioContext();
    const source = context.createMediaStreamSource(localStreamRef.current);
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    const data = new Uint8Array(analyser.frequencyBinCount);
    source.connect(analyser);

    const tick = () => {
      if (disposed) return;
      analyser.getByteFrequencyData(data);
      const average = data.reduce((sum, value) => sum + value, 0) / data.length;
      const speaking = canSendAudio && average > 18;
      const now = Date.now();
      if (speaking !== lastSpeaking || now - lastSentAt > 1500) {
        lastSpeaking = speaking;
        lastSentAt = now;
        setLocalSpeaking(speaking);
        sendVoiceState({ isSpeaking: speaking });
      }
      animationFrame = window.requestAnimationFrame(tick);
    };
    void context.resume().then(tick);
    return () => {
      disposed = true;
      window.cancelAnimationFrame(animationFrame);
      source.disconnect();
      void context.close();
      setLocalSpeaking(false);
      sendVoiceState({ isSpeaking: false });
    };
  }, [canSendAudio, sendVoiceState, settings?.voiceActivityDetection]);

  useEffect(() => () => {
    closeAllPeers();
    for (const track of localStreamRef.current?.getTracks() ?? []) track.stop();
    if (socketReady) sendSocketMessage({ type: "voice:leave" });
  }, [closeAllPeers, roomId, sendSocketMessage, socketReady]);

  return {
    access,
    participants,
    activeParticipants,
    browserSupported,
    voiceEnabled,
    micEnabled,
    mutedBySelf,
    audioPermissionGranted,
    canSendAudio,
    pushToTalkPressed,
    setPushToTalkPressed,
    localSpeaking,
    someoneSpeaking,
    remoteStreams,
    masterVolume,
    setMasterVolume,
    peerVolumes,
    setPeerVolume,
    startVoice,
    leaveVoice,
    toggleMute,
    requestMicrophone,
    error,
    playerNames,
  };
}

export type VoiceChatController = ReturnType<typeof useVoiceChat>;
